import { cn } from '@/lib/utils'

import { Skeleton, usePromoBlockVariants } from '../../../Theme'

import type { PromoBlockSkeletonProps } from '../../types'

/**
 * PromoBlockSkeleton - Loading state for promo block
 *
 * Features:
 * - Matches PromoBlock layout (image + content side by side)
 * - Responsive: stacked on mobile, side-by-side on desktop
 * - Consistent dimensions with actual component
 * - Accessible: uses aria-busy for screen readers
 * - CVA variants via UiProvider for consistent styling
 *
 * @example
 * <PromoBlockSkeleton />
 */
export function PromoBlockSkeleton({ className, imagePosition = 'left' }: PromoBlockSkeletonProps) {
  const {
    promoBlockSectionVariants,
    promoBlockContainerVariants,
    promoBlockImageSectionVariants,
    promoBlockSkeletonContentVariants,
    promoBlockSkeletonInnerVariants,
  } = usePromoBlockVariants()

  const imageSkeleton = (
    <div className={promoBlockImageSectionVariants()}>
      <Skeleton className="w-full h-full min-h-[400px] md:min-h-[500px]" />
    </div>
  )

  const contentSkeleton = (
    <div className={promoBlockSkeletonContentVariants()}>
      <div className={promoBlockSkeletonInnerVariants()}>
        {/* Title Skeleton */}
        <Skeleton className="h-10 w-3/4 mx-auto md:mx-0" />

        {/* Description Skeleton */}
        <div className="space-y-3">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-5/6 mx-auto md:mx-0" />
        </div>

        {/* Button Skeleton */}
        <Skeleton className="h-12 w-40 mx-auto md:mx-0" />
      </div>
    </div>
  )

  return (
    <section
      className={cn(promoBlockSectionVariants(), className)}
      aria-busy="true"
      aria-label="Loading promotional content"
    >
      <div className={promoBlockContainerVariants({ imagePosition })}>
        {imageSkeleton}
        {contentSkeleton}
      </div>
    </section>
  )
}
